import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Lock, Eye, EyeOff, ArrowRight, ShieldCheck } from "lucide-react";
import poolMoneyLogo from "../../assets/new-revamp/header/poolMoneyWhite.png";

const PASSCODE = "IWILLINVEST";
const SESSION_KEY = "pm_investor_auth";

/* ═══════════════════════════════════════════════════
   InvestorGate
   Wraps any children behind a passcode wall.
   • Reads ?code= from URL on mount — auto-unlocks if correct
   • Falls back to sessionStorage for refreshes
   • Otherwise shows the lock screen
═══════════════════════════════════════════════════ */
export default function InvestorGate({ children }) {
  // Password temporarily disabled — renders children directly.
  // To re-enable, restore the lock screen logic below.
  return <>{children}</>;
}

/* ═══════════════════════════════════════════════════
   Lock screen
═══════════════════════════════════════════════════ */
function LockScreen({ onUnlock }) {
  const [value, setValue] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState(false);
  const [shake, setShake] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const attempt = () => {
    if (value.trim().toUpperCase() === PASSCODE) {
      sessionStorage.setItem(SESSION_KEY, "1");
      onUnlock();
    } else {
      setError(true);
      setShake(true);
      setTimeout(() => setShake(false), 500);
      setTimeout(() => setError(false), 2000);
      setValue("");
    }
  };

  const handleKey = (e) => {
    if (e.key === "Enter") attempt();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      className="fixed inset-0 z-[999] flex items-center justify-center bg-gradient-to-br from-[#0d0520] via-[#1e0a4a] to-[#2a1466] px-5"
    >
      {/* Background blobs */}
      <div className="pointer-events-none absolute -left-32 -top-32 h-96 w-96 rounded-full bg-primary/20 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 -right-24 h-80 w-80 rounded-full bg-violet-500/15 blur-3xl" />
      <div className="pointer-events-none absolute left-1/2 top-1/4 h-64 w-64 -translate-x-1/2 rounded-full bg-purple-600/10 blur-3xl" />

      <motion.div
        animate={shake ? { x: [-8, 8, -6, 6, -4, 4, 0] } : {}}
        transition={{ duration: 0.45 }}
        className="relative w-full max-w-md"
      >
        {/* Card */}
        <div className="overflow-hidden rounded-3xl border border-white/10 bg-white/[0.04] shadow-[0_40px_100px_-20px_rgba(76,66,253,0.5)] backdrop-blur-xl">

          {/* Top accent */}
          <div className="h-px w-full bg-gradient-to-r from-transparent via-white/30 to-transparent" />

          <div className="px-8 py-10 sm:px-10">

            {/* Logo */}
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05, duration: 0.4 }}
              className="mb-8 flex justify-center"
            >
              <img
                src={poolMoneyLogo}
                alt="PoolMoney"
                className="h-28 w-auto object-contain opacity-90"
              />
            </motion.div>

            {/* Icon */}
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.1, type: "spring", stiffness: 260, damping: 20 }}
              className="mx-auto mb-7 flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-violet-600 shadow-[0_8px_32px_rgba(76,66,253,0.5)]"
            >
              <Lock size={18} strokeWidth={1.8} className="text-white" />
            </motion.div>

            {/* Heading */}
            <div className="text-center">
              <h1 className="text-[1.5rem] font-black tracking-[-0.02em] text-white sm:text-[1.7rem]">
                Investor Deck
              </h1>
              <p className="mt-2 text-[0.82rem] leading-[1.7] text-white/50">
                This page is private. Enter your access code to continue.
              </p>
            </div>

            {/* Input */}
            <div className="mt-8">
              <div className={`relative overflow-hidden rounded-2xl border transition-all duration-200 ${
                error
                  ? "border-red-400/60 shadow-[0_0_0_3px_rgba(248,113,113,0.15)]"
                  : "border-white/15 focus-within:border-primary/60 focus-within:shadow-[0_0_0_3px_rgba(76,66,253,0.15)]"
              }`}>
                <input
                  ref={inputRef}
                  type={show ? "text" : "password"}
                  value={value}
                  onChange={(e) => { setValue(e.target.value); setError(false); }}
                  onKeyDown={handleKey}
                  placeholder="Enter access code"
                  spellCheck={false}
                  autoComplete="off"
                  className="w-full bg-white/[0.06] px-5 py-4 pr-12 text-[0.95rem] font-bold tracking-[0.08em] text-white placeholder:text-white/25 placeholder:font-normal placeholder:tracking-normal outline-none"
                />
                <button
                  type="button"
                  onClick={() => setShow((v) => !v)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white/30 transition-colors hover:text-white/70"
                >
                  {show
                    ? <EyeOff size={16} strokeWidth={2} />
                    : <Eye size={16} strokeWidth={2} />
                  }
                </button>
              </div>

              {/* Error message */}
              <AnimatePresence>
                {error && (
                  <motion.p
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className="mt-2 text-center text-[0.72rem] font-semibold text-red-400"
                  >
                    Incorrect access code. Please try again.
                  </motion.p>
                )}
              </AnimatePresence>

              {/* Submit */}
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.97 }}
                onClick={attempt}
                className="mt-4 flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-primary to-violet-600 px-6 py-4 text-[0.88rem] font-black text-white shadow-[0_8px_24px_rgba(76,66,253,0.4)] transition-shadow hover:shadow-[0_12px_32px_rgba(76,66,253,0.55)]"
              >
                Access Deck
                <ArrowRight size={16} strokeWidth={2.5} />
              </motion.button>
            </div>

            {/* Footer note */}
            <div className="mt-8 flex items-center justify-center gap-2 text-[0.65rem] text-white/25">
              <ShieldCheck size={12} strokeWidth={2} />
              <span>Private & confidential · PoolMoney</span>
            </div>

          </div>

          {/* Bottom accent */}
          <div className="h-px w-full bg-gradient-to-r from-transparent via-white/10 to-transparent" />
        </div>
      </motion.div>
    </motion.div>
  );
}
